import React, { createContext, useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";

type StrategyMode = "equities_fx" | "prediction_market";

interface StrategyModeContextType {
  mode: StrategyMode;
  setMode: (mode: StrategyMode) => void;
  welcomeModalOpen: boolean;
  setWelcomeModalOpen: (open: boolean) => void;
  hasEventAnalyzed: boolean;
  setHasEventAnalyzed: (val: boolean) => void;
  predictionSearchValue: string;
  setPredictionSearchValue: (val: string) => void;
  predictionSearchTrigger: number;
  triggerPredictionSearch: () => void;
  predictionSearchLoading: boolean;
  setPredictionSearchLoading: (val: boolean) => void;
}

const StrategyModeContext = createContext<StrategyModeContextType | undefined>(
  undefined,
);

export const StrategyModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const router = useRouter();
  const [userId, setUserId] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("user-info");
      if (stored) {
        try {
          return JSON.parse(stored).user_id || "";
        } catch (e) {}
      }
    }
    return "";
  });
  
  const [mode, setModeState] = useState<StrategyMode>(() => {
    if (typeof window !== "undefined") {
      const userInfoStr = localStorage.getItem("user-info");
      let uId = "";
      if (userInfoStr) {
        try {
          uId = JSON.parse(userInfoStr).user_id || "";
        } catch (e) {}
      }
      const savedMode = localStorage.getItem(`strategy_mode_${uId}`) as StrategyMode | null;
      if (savedMode === "equities_fx" || savedMode === "prediction_market") {
        return savedMode;
      }
    }
    return "equities_fx";
  });
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [hasEventAnalyzed, setHasEventAnalyzed] = useState(false);
  const [predictionSearchValue, setPredictionSearchValue] = useState("");
  const [predictionSearchTrigger, setPredictionSearchTrigger] = useState(0);
  const [predictionSearchLoading, setPredictionSearchLoading] = useState(false);

  useEffect(() => {
    if (router.isReady && router.query.mode === "prediction_market") {
      setMode("prediction_market");
    }
  }, [router.isReady, router.query.mode]);

  // Periodically check local storage for user ID changes (auth changes)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkUserId = () => {
      const stored = localStorage.getItem("user-info");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const currentId = parsed.user_id || "";
          if (currentId !== userId) {
            setUserId(currentId);
          }
        } catch (e) {
          if (userId !== "") setUserId("");
        }
      } else {
        if (userId !== "") setUserId("");
      }
    };

    checkUserId();
    const interval = setInterval(checkUserId, 1000);
    return () => clearInterval(interval);
  }, [userId]);

  // Load persistent mode when user ID resolves or changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    let targetMode: StrategyMode = "equities_fx";
    const storageKey = `strategy_mode_${userId}`;
    const savedMode = localStorage.getItem(storageKey) as StrategyMode | null;

    if (savedMode === "equities_fx" || savedMode === "prediction_market") {
      targetMode = savedMode;
    }
    setModeState(targetMode);
  }, [userId]);


  const setMode = (newMode: StrategyMode) => {
    setModeState(newMode);
    if (typeof window !== "undefined") {
      const storageKey = `strategy_mode_${userId}`;
      localStorage.setItem(storageKey, newMode);
    }
  };

  const triggerPredictionSearch = () => {
    setPredictionSearchTrigger((prev) => prev + 1);
  };

  return (
    <StrategyModeContext.Provider
      value={{
        mode,
        setMode,
        welcomeModalOpen,
        setWelcomeModalOpen,
        hasEventAnalyzed,
        setHasEventAnalyzed,
        predictionSearchValue,
        setPredictionSearchValue,
        predictionSearchTrigger,
        triggerPredictionSearch,
        predictionSearchLoading,
        setPredictionSearchLoading,
      }}
    >
      {children}
    </StrategyModeContext.Provider>
  );
};

export const useStrategyMode = () => {
  const context = useContext(StrategyModeContext);
  if (!context) {
    throw new Error(
      "useStrategyMode must be used within a StrategyModeProvider",
    );
  }
  return context;
};
